import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrganization } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Building2, 
  ArrowLeft,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import LossRunInsights from '../components/LossRunInsights';

export default function OrganizationDetail() {
  const { id: orgId } = useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const orgRes = await getOrganization(orgId);
      setOrganization(orgRes.data);
    } catch (error) {
      toast.error('Failed to fetch organization data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
        <Building2 className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg">Organization not found</p>
        <Button variant="link" onClick={() => navigate('/organizations')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="organization-detail-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Button 
          variant="ghost" 
          className="w-fit" 
          onClick={() => navigate('/organizations')}
          data-testid="back-to-orgs-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Organizations
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-secondary/50 flex items-center justify-center overflow-hidden">
              {organization.logo_url ? (
                <>
                  <img src={organization.logo_url} alt={organization.name} className="w-full h-full object-cover rounded-lg" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  <div className="w-full h-full items-center justify-center hidden">
                    <Building2 className="w-8 h-8 text-muted-foreground" />
                  </div>
                </>
              ) : (
                <Building2 className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
                {organization.name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="capitalize">
                  {organization.org_type?.replace('_', ' ') || 'Sports'}
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {organization.level}
                </Badge>
                {organization.lifecycle_status && (
                  <Badge variant="secondary" className="capitalize">
                    {organization.lifecycle_status.replace('_', ' ')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Info Card */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Organization Information
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {organization.contact_email && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>{organization.contact_email}</span>
                </div>
              )}
              {organization.contact_phone && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-4 h-4" />
                  <span>{organization.contact_phone}</span>
                </div>
              )}
            </div>
            <div className="space-y-4">
              {(organization.address || organization.city || organization.state) && (
                <div className="flex items-start gap-3 text-muted-foreground">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <div>
                    {organization.address && <p>{organization.address}</p>}
                    {(organization.city || organization.state || organization.zip_code) && (
                      <p>
                        {[organization.city, organization.state, organization.zip_code]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Risk Score if available */}
          {organization.risk_score !== undefined && organization.risk_score !== null && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-semibold mb-3">Risk Assessment</h4>
              <div className="flex items-center gap-4">
                <div className={`text-3xl font-bold ${
                  organization.risk_level === 'low' ? 'text-green-600' :
                  organization.risk_level === 'medium' ? 'text-yellow-600' :
                  organization.risk_level === 'elevated' ? 'text-orange-600' :
                  'text-red-600'
                }`}>
                  {organization.risk_score?.toFixed(0)}%
                </div>
                <Badge className={
                  organization.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                  organization.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  organization.risk_level === 'elevated' ? 'bg-orange-100 text-orange-800' :
                  'bg-red-100 text-red-800'
                }>
                  {organization.risk_level === 'medium' ? 'Moderate' : organization.risk_level?.charAt(0).toUpperCase() + organization.risk_level?.slice(1)} Risk
                </Badge>
              </div>
            </div>
          )}

          {/* GL Policy Renewal Date */}
          {organization.gl_policy_renewal_date && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-semibold mb-2">GL Policy Renewal Date</h4>
              <p className="text-muted-foreground">
                {new Date(organization.gl_policy_renewal_date).toLocaleDateString()}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loss Run AI Insights (auto-hides if no loss runs exist) */}
      <LossRunInsights orgId={orgId} />
    </div>
  );
}
